import type {
  OrganizationImportPreview,
  OrganizationImportPreviewRow,
  OrganizationImportResult,
} from '@lms/shared'
import { ORGANIZATION_CSV_TEMPLATE_HEADERS } from '@lms/shared'
import { AlertCircle, CheckCircle2, FileUp, Loader2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { readCsvFile } from '@/lib/csv'

type Step = 'upload' | 'preview' | 'confirm' | 'result'

interface CsvImportFlowProps {
  title?: string
  onPreview: (csvText: string) => Promise<OrganizationImportPreview>
  onImport: (csvText: string) => Promise<OrganizationImportResult>
  previewColumns: { key: keyof OrganizationImportPreviewRow; label: string }[]
}

export function CsvImportFlow({
  title = 'CSVインポート',
  onPreview,
  onImport,
  previewColumns,
}: CsvImportFlowProps) {
  const [step, setStep] = useState<Step>('upload')
  const [csvText, setCsvText] = useState('')
  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<OrganizationImportPreview | null>(null)
  const [result, setResult] = useState<OrganizationImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setError(null)
    try {
      setCsvText(await readCsvFile(file))
    } catch {
      setError('ファイルを読み込めませんでした。文字コードを確認してください。')
    }
  }

  const handlePreview = async () => {
    if (!csvText) return
    setLoading(true)
    setError(null)
    try {
      const data = await onPreview(csvText)
      setPreview(data)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'プレビューに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    if (!csvText) return
    setLoading(true)
    setError(null)
    try {
      const data = await onImport(csvText)
      setResult(data)
      setStep('result')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'インポートに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const reset = () => {
    setStep('upload')
    setCsvText('')
    setFileName('')
    setPreview(null)
    setResult(null)
    setError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const renderRow = (row: OrganizationImportPreviewRow, kind: '追加' | '更新', key: string) => (
    <TableRow key={key}>
      <TableCell className={kind === '追加' ? 'text-green-600 text-xs' : 'text-blue-600 text-xs'}>
        {kind}
        {row.isSupervisorOnly && (
          <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-700">上司</span>
        )}
        {row.isNewUser && (
          <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] text-slate-600">新規</span>
        )}
      </TableCell>
      {previewColumns.map((col) => (
        <TableCell key={col.key} className="text-xs">
          {String(row[col.key] ?? '')}
        </TableCell>
      ))}
    </TableRow>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
              <p>対応する列（カンマ区切り・タブ区切りのどちらでも可）:</p>
              <p className="font-mono text-[11px] text-foreground">
                {ORGANIZATION_CSV_TEMPLATE_HEADERS.join(' / ')}
              </p>
              <p>
                「社員コード」「社員メールアドレス」が必須です。文字コードは UTF-8 / Shift_JIS
                に対応しています。
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => fileRef.current?.click()}>
                <FileUp className="mr-2 h-4 w-4" />
                CSVファイルを選択
              </Button>
              {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv,.txt"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            <Button onClick={handlePreview} disabled={!csvText || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              プレビュー
            </Button>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-green-600">追加: {preview.toAdd.length}件</span>
              <span className="text-blue-600">更新: {preview.toUpdate.length}件</span>
              <span className="text-muted-foreground">変更なし: {preview.unchanged}件</span>
              {preview.errors.length > 0 && (
                <span className="text-red-600">エラー: {preview.errors.length}件</span>
              )}
            </div>

            {(preview.toAdd.length > 0 || preview.toUpdate.length > 0) && (
              <div className="max-h-64 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">種別</TableHead>
                      {previewColumns.map((col) => (
                        <TableHead key={col.key}>{col.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.toAdd.map((row, i) => renderRow(row, '追加', `add-${i}`))}
                    {preview.toUpdate.map((row, i) => renderRow(row, '更新', `update-${i}`))}
                  </TableBody>
                </Table>
              </div>
            )}

            {preview.errors.length > 0 && (
              <div className="max-h-48 overflow-auto rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                {preview.errors.map((err, i) => (
                  <p key={`err-${err.row}-${i}`} className="text-xs text-red-700">
                    行 {err.row}: {err.message}
                  </p>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={() => setStep('confirm')}
                disabled={preview.toAdd.length === 0 && preview.toUpdate.length === 0}
              >
                インポート実行
              </Button>
              <Button variant="outline" onClick={reset}>
                やり直す
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-4">
            <p className="text-sm">
              追加 {preview?.toAdd.length ?? 0}件、更新 {preview?.toUpdate.length ?? 0}件
              をインポートします。よろしいですか？
            </p>
            {(preview?.toAdd.some((r) => r.isNewUser) ||
              preview?.toUpdate.some((r) => r.isNewUser)) && (
              <p className="text-xs text-muted-foreground">
                「新規」の社員はまだLMSにログインしていないため、メールアドレスで仮ユーザーを作成します。
                初回ログイン時に同じアカウントへ紐付きます。
              </p>
            )}
            <div className="flex gap-2">
              <Button onClick={handleImport} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                実行
              </Button>
              <Button variant="outline" onClick={() => setStep('preview')}>
                戻る
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">インポート完了</span>
            </div>
            <div className="text-sm space-y-1">
              <p>対象行: {result.totalRows}件</p>
              <p>追加: {result.insertedCount}件</p>
              <p>更新: {result.updatedCount}件</p>
              {result.createdUserCount > 0 && <p>仮ユーザー作成: {result.createdUserCount}件</p>}
              {result.skippedCount > 0 && (
                <p className="text-red-600">スキップ: {result.skippedCount}件</p>
              )}
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-48 overflow-auto rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                {result.errors.map((err, i) => (
                  <p key={`result-err-${err.row}-${i}`} className="text-xs text-red-700">
                    行 {err.row}: {err.message}
                  </p>
                ))}
              </div>
            )}
            <Button variant="outline" onClick={reset}>
              新しいインポート
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
